import type { ComputedRef } from "vue";
import { computed } from "vue";

import type { UnitPresets } from "@/libs/units/presets";
import type { ConvertableUnit } from "@/libs/units";
import { effectiveDifferentialPressureUnit } from "@/libs/units/pressure";

const DIFFERENTIAL_PRESSURE_OVERRIDE_KEY = "Differential Pressure.convert.PSIA";

export const useTargetUnits = (
  getUnitByKey: (
    genericKey: keyof UnitPresets,
    overrideKey?: string
  ) => ComputedRef<ConvertableUnit>
) => {
  const differentialTargetCache = new Map<
    string,
    ComputedRef<ConvertableUnit>
  >();
  const differentialModelCache = new Map<
    string,
    ComputedRef<ConvertableUnit[]>
  >();

  const getDifferentialPressureTargetUnit = (overrideKey?: string) => {
    const key = overrideKey ?? DIFFERENTIAL_PRESSURE_OVERRIDE_KEY;
    if (!differentialTargetCache.has(key)) {
      differentialTargetCache.set(
        key,
        computed(() =>
          effectiveDifferentialPressureUnit(
            getUnitByKey("Generic.Pressure", key).value
          )
        )
      );
    }
    return differentialTargetCache.get(key)!;
  };

  const getDifferentialPressureModel = (overrideKey?: string) => {
    const key = overrideKey ?? DIFFERENTIAL_PRESSURE_OVERRIDE_KEY;
    if (!differentialModelCache.has(key)) {
      differentialModelCache.set(
        key,
        computed(() => [getDifferentialPressureTargetUnit(overrideKey).value])
      );
    }
    return differentialModelCache.get(key)!;
  };

  return {
    getDifferentialPressureTargetUnit,
    getDifferentialPressureModel,

    getLargeLengthUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Length.Large", overrideKey),

    getSmallLengthUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Length.Small", overrideKey),

    getLiquidFlowUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.FlowRate.Liquid", overrideKey),

    getGasFlowUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.FlowRate.Gas", overrideKey),

    getProductivityFlowUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.VolumeRate.PI", overrideKey),

    getPressureUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Pressure", overrideKey),

    getTempUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Temperature", overrideKey),

    getDensityUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Density", overrideKey),

    getCurrentUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Current", overrideKey),

    getSpeedUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Speed", overrideKey),

    getVoltageUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Voltage", overrideKey),

    getViscosityUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Viscosity", overrideKey),

    getVolumeUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Volume", overrideKey),

    getLiquidRatioUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Ratio.Liquid", overrideKey),

    getMassUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Mass", overrideKey),

    getHydraulicPowerUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Power.Hydraulic", overrideKey),

    getRealPowerUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Power.Real", overrideKey),

    getApparentPowerUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Power.Apparent", overrideKey),

    getConcentrationUnit: (overrideKey?: string) =>
      getUnitByKey("Generic.Concentration", overrideKey)
  };
};
